import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import uniqBy from 'lodash/uniqBy';
import orderBy from 'lodash/orderBy';
import PartsDataProvider from './PartsDataProvider';
import OtherPartsProvider from './OtherPartsProvider';

const { arrayOf, shape, string, any, bool, array } = PropTypes;

/**
 * Provides data response
 * */
class ResourcesProvider extends PureComponent {
  static displayName = 'ResourcesProvider';

  static propTypes = {
    /**
     * Assignment details
     * - the ID of the assignment
     * - the list of questions students associated to it
     * - the list of students associated to it.
     * */
    assignment: shape({
      id: PropTypes.string.isRequired,
      questions: array.isRequired,
    }).isRequired,
    /**
     * Table th column info
     * */
    rawColumns: arrayOf(
      shape({
        /**
         * Column label
         * */
        label: string,
        /**
         * Column className
         * */
        className: string,
        /**
         * Children
         * */
        children: any,
        /**
         * Used for identifying the current part column with no label
         * Used for identifying the current part column with no label
         * Provider will add the label for column
         * */
        isCurrentPart: bool,
      }),
    ).isRequired,
    /**
     * Assignment has frq questions bool
     * */
    hasFrqQuestions: bool,
    /**
     * Course outline ppc tag pairs identifiers
     * */
    ppcTagPairIdentifiers: string.isRequired,
    /**
     * List of student taking this assignment (assigned to the class)
     * */
    students: array.isRequired,
    /**
     * Student context
     * */
    studentContext: shape({
      hasContext: bool,
      studentId: string,
      resultsPath: string,
      progressPath: string,
      questionsPath: string,
    }),
  };

  static defaultProps = {
    hasFrqQuestions: false,
    studentId: null,
    studentContext: {
      hasContext: false,
      studentId: null,
      resultsPath: '',
      progressPath: '',
      questionsPath: '',
    },
  };

  /**
   * Parse raw data for items and col identifiers
   * @param {string} tagPairs
   * @return {array} items Reorganized items
   * */
  parseRawData = (tagPairs) => {
    let tagPairsList;
    try {
      tagPairsList = JSON.parse(tagPairs);
    } catch (err) {
      tagPairsList = [];
    }

    const { hasUniqueItems, ppcTagPairIdentifiers } = this.props;

    if (hasUniqueItems) {
      tagPairsList = uniqBy(
        tagPairsList,
        `${ppcTagPairIdentifiers}.displayName`,
      );
      tagPairsList = orderBy(tagPairsList, ['abbrv']);
    }

    let items = [];
    let cols = [];
    if (ppcTagPairIdentifiers) {
      cols = ppcTagPairIdentifiers.split(',');
      (tagPairsList || []).forEach((row) => {
        const { abbrv } = row;
        let currentItemRow = {
          abbreviation: abbrv,
        };
        cols.forEach((col) => {
          const { displayName, order } = row[col];
          currentItemRow[col] = {
            label: displayName,
            col,
            order,
          };
        });

        items.push(currentItemRow);
      });
    }

    return items;
  };

  reloadComponent = () => {
    /**
     * @todo: Fix this
     * */
    window.location.reload();
  };

  render() {
    const {
      tagPairsData: { assignmentProgressWithTagPairs },
      children,
      rawColumns,
      hasFrqQuestions,
      students: classStudents,
      assignment,
      learnosityReportsByItemData,
      studentContext,
    } = this.props;

    const {
      tagPairs,
      assessmentShortName,
      learnosityActivityId: currentPartActivityId,
      resultByTags,
      otherPartsResultsByItem,
      otherParts,
    } = assignmentProgressWithTagPairs;
    const rawItems = this.parseRawData(tagPairs);
    const otherProps = {
      assessmentShortName,
      currentPartActivityId,
    };
    const { students: assignmentStudents, isIndividualAssignment } = assignment;
    const students =
      isIndividualAssignment && assignmentStudents
        ? assignmentStudents
        : classStudents;

    return (
      <OtherPartsProvider otherPartsResultsByItem={otherPartsResultsByItem}>
        <PartsDataProvider
          studentContext={studentContext}
          currentPartLabel={assessmentShortName}
          currentPartActivityId={currentPartActivityId}
          resultByTags={resultByTags}
          hasFrqQuestions={hasFrqQuestions}
          rawColumns={rawColumns}
          rawItems={rawItems}
          otherPartsList={otherParts}
          students={students}
          assignment={assignment}
          learnosityReportsByItemData={learnosityReportsByItemData}
          onAssign={this.reloadComponent}
          onAssignRedirect={false}
          assignmentProgressWithTagPairs={assignmentProgressWithTagPairs}
        >
          {React.Children.map(
            children,
            (child) => child && React.cloneElement(child, otherProps),
          )}
        </PartsDataProvider>
      </OtherPartsProvider>
    );
  }
}

export default ResourcesProvider;
